import React, { useEffect } from 'react';
import { Palette, hexToRgba, lighten, darken, isDarkBg } from './themePalette';
import { getBulletChar } from '../components/LiveSlidePreview';

// Mirror of renderInlineMarkdown in AssemblingFlowLoading.tsx — kept local
// so PreviewOverlay doesn't pull the loading module into its boundary.
function renderInlineMarkdown(text: string, accent: string): React.ReactNode {
  if (!text) return text;
  const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
  const out: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let k = 0;
  while ((match = linkPattern.exec(text)) !== null) {
    if (match.index > lastIndex) out.push(text.slice(lastIndex, match.index));
    out.push(
      <span
        key={`link-${k++}`}
        style={{
          color: accent,
          textDecoration: 'underline',
          textUnderlineOffset: 2,
          textDecorationThickness: 1,
        }}
      >
        {match[1]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex));
  return out.length > 0 ? out : text;
}

export interface PreviewScreen {
  n: number;
  title: string;
  body?: string;
  bullets?: string[];
}

interface PreviewOverlayProps {
  screen: PreviewScreen;
  theme: Palette;
  themeId: string | null;
  variant: 'desktop' | 'mobile';
  onClose: () => void;
}

/**
 * Shared body renderer: bullets-as-list when present, plain paragraph otherwise.
 * Keeps the mobile and desktop variants visually in sync.
 */
function PreviewBody({ screen, theme, themeId }: { screen: PreviewScreen; theme: Palette; themeId: string | null }) {
  if (screen.bullets && screen.bullets.length > 0) {
    return (
      <ul
        style={{
          margin: 0,
          padding: 0,
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          textAlign: 'left',
          maxWidth: 360,
          alignSelf: 'center',
        }}
      >
        {screen.bullets.map((item, i) => (
          <li
            key={i}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              fontSize: 14,
              lineHeight: 1.55,
              color: theme.secondary,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                flexShrink: 0,
                color: theme.accent,
                fontWeight: 700,
                lineHeight: 1.55,
                minWidth: '1em',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {getBulletChar(themeId || undefined, i)}
            </span>
            <span style={{ flex: 1 }}>{renderInlineMarkdown(item, theme.accent)}</span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p
      style={{
        margin: 0,
        fontSize: 14,
        lineHeight: 1.65,
        color: theme.secondary,
        textAlign: 'center',
      }}
    >
      {screen.body ? renderInlineMarkdown(screen.body, theme.accent) : 'Generated content for this screen.'}
    </p>
  );
}

export function PreviewOverlay({ screen, theme, themeId, variant, onClose }: PreviewOverlayProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const darkTheme = isDarkBg(theme.bg);
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  if (variant === 'mobile') {
    return (
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 100,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'flex-end',
          animation: 'asm-fadeIn 0.18s ease',
        }}
      >
        <div
          onClick={stop}
          style={{
            width: '100%',
            background: theme.bg,
            borderTopLeftRadius: 26,
            borderTopRightRadius: 26,
            padding: '10px 0 36px',
            position: 'relative',
            boxShadow: '0 -20px 60px -10px rgba(0,0,0,0.5)',
            animation: 'asm-slideUpMobile 0.24s cubic-bezier(0.2, 0.8, 0.2, 1)',
            maxHeight: '82%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 6 }}>
            <div
              style={{
                width: 38,
                height: 4,
                borderRadius: 2,
                background: darkTheme ? 'rgba(255,255,255,0.20)' : 'rgba(0,0,0,0.18)',
              }}
            />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 22px 4px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  color: theme.secondary,
                  letterSpacing: '0.10em',
                  opacity: 0.8,
                }}
              >
                SCREEN {String(screen.n).padStart(2, '0')}
              </span>
              <svg width="11" height="11" viewBox="0 0 16 16">
                <path
                  d="M3.5 8.5l3 3 6-6.5"
                  stroke={theme.accent}
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              className="asm-overlay-close"
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: darkTheme ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                color: theme.secondary,
                fontSize: 16,
              }}
            >
              ×
            </button>
          </div>

          <div style={{ padding: '28px 28px 12px', flex: 1, overflow: 'auto' }}>
            <h1
              style={{
                margin: '0 0 16px',
                fontSize: 24,
                lineHeight: 1.22,
                fontWeight: 700,
                color: theme.primary,
                letterSpacing: '-0.01em',
                textAlign: 'center',
              }}
            >
              {screen.title}
            </h1>
            <PreviewBody screen={screen} theme={theme} themeId={themeId} />
          </div>

          <div
            style={{
              margin: '16px auto 0',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              borderRadius: 999,
              background: hexToRgba(theme.accent, 0.10),
              border: `1px dashed ${hexToRgba(theme.accent, 0.35)}`,
              alignSelf: 'center',
            }}
          >
            <ImageHintIcon color={theme.accent} />
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: theme.secondary,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              Imagery generates in the editor
            </span>
          </div>

          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 3,
              background: theme.accent,
              opacity: 0.7,
            }}
          />
        </div>
      </div>
    );
  }

  // Desktop variant — centered card
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
        animation: 'asm-fadeIn 0.18s ease',
      }}
    >
      <div
        onClick={stop}
        style={{
          width: 420,
          maxHeight: '90%',
          borderRadius: 20,
          background: theme.bg,
          position: 'relative',
          boxShadow: `0 0 0 1px ${hexToRgba(theme.accent, 0.25)}, 0 40px 80px -20px rgba(0,0,0,0.6)`,
          overflow: 'hidden',
          animation: 'asm-slideUp 0.22s cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close preview"
          className="asm-overlay-close"
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            zIndex: 5,
            width: 30,
            height: 30,
            borderRadius: '50%',
            background: darkTheme ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)',
            color: theme.secondary,
            fontSize: 16,
          }}
        >
          ×
        </button>

        <div
          style={{
            position: 'absolute',
            top: 18,
            left: 22,
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 11,
            fontWeight: 700,
            color: theme.secondary,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '0.10em',
            opacity: 0.75,
          }}
        >
          <span>SCREEN {String(screen.n).padStart(2, '0')}</span>
          <svg width="11" height="11" viewBox="0 0 16 16">
            <path
              d="M3.5 8.5l3 3 6-6.5"
              stroke={theme.accent}
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div
          style={{
            padding: '72px 36px 56px',
            minHeight: 540,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <h1
            style={{
              margin: '0 0 22px 0',
              fontSize: 26,
              lineHeight: 1.2,
              fontWeight: 700,
              color: theme.primary,
              letterSpacing: '-0.01em',
              textAlign: 'center',
            }}
          >
            {screen.title}
          </h1>
          <PreviewBody screen={screen} theme={theme} themeId={themeId} />

          <div
            style={{
              marginTop: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '10px 14px',
              borderRadius: 999,
              background: hexToRgba(theme.accent, 0.10),
              border: `1px dashed ${hexToRgba(theme.accent, 0.35)}`,
              alignSelf: 'center',
            }}
          >
            <ImageHintIcon color={theme.accent} />
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: theme.secondary,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              Imagery generates when you open the editor
            </span>
          </div>
        </div>

        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: theme.accent,
            opacity: 0.7,
          }}
        />
      </div>
    </div>
  );
}

function ImageHintIcon({ color }: { color: string }) {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2.5" width="12" height="10" rx="1.5" stroke={color} strokeWidth="1.5" />
      <circle cx="5.5" cy="6" r="1" fill={color} />
      <path
        d="M3 11l3.5-3.5L9 10l2-2 2 2"
        stroke={color}
        strokeWidth="1.3"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Helper to keep unused-import linter happy if lighten/darken are tree-shaken later
void lighten;
void darken;
